import React, { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { phoneNumber as phoneNumberHelpers } from '@nxt-insurance/helpers'

import { IMAGE } from '@ui-kit/assets/images/index.web'
import Icon from '@ui-kit/components/Icon/index.web'
import MenuContainerComponent from '@ui-kit/components/formComponents/Select/components/MenuContainer'
import OptionsContainerComponent from '@ui-kit/components/formComponents/Select/components/OptionsContainer'
import type { OptionValue } from '@ui-kit/components/formComponents/Select/types'
import { getOptionIdentifier } from '@ui-kit/components/formComponents/Select/utils'
import TextField from '@ui-kit/components/formComponents/TextField/index.web'
import TextFieldContainer from '@ui-kit/components/formComponents/TextFieldContainer/index.web'
import { textInput } from '@ui-kit/components/formComponents/TextFieldContainer/styles'
import { FORM_ELEMENT } from '@ui-kit/constants'
import { caption } from '@ui-kit/core/typography'
import { isFieldValueSet } from '@ui-kit/utils/formComponentUtils'

import type { WebProps } from './types'
import {
  displaySelectedOption,
  getDefaultCountry,
  getDialCodeFromCountryCode,
  getOptions,
} from './utils'

/*
  PhoneNumberInput component is a custom component for providing phone number
  Allowing user to select a country code from available list of countries.
  This component renders in this way
  <div>
    <Select/> // for selecting country code
    <TextField /> // for entering phone number
    <Options /> // list of available countries
    <ErrorMessages /> // error message
  </div>

  Unfortunately it was not possible to use general Select component due to css limitation
  So there is a custom implementation of select with reusable parts of general Select component
 */
const PhoneNumberInput = (props: WebProps) => {
  const [isFilled, setIsFilled] = useState(isFieldValueSet(props.value))
  const [isFocused, setIsFocused] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [activeMenuOption, setActiveMenuOption] = useState('')
  const options = getOptions(props.defaultCountry, props.countries)
  const [phoneNumberCode, setPhoneNumberCode] = useState(
    getDefaultCountry(props.countries, props.defaultCountry)?.code ?? undefined
  )
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined)

  const onChangeFilterInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value)

    setActiveMenuOption(getOptionIdentifier(event.target.value, props.name))
  }

  const onSelectOptionHandler = (newValue: OptionValue) => {
    if (props.disabled || newValue === props.value) {
      return
    }

    setPhoneNumberCode(newValue as string)
    setFilterValue('')
    setIsFocused(false)
    setIsFilled(isFieldValueSet(newValue))

    const currentDialCode = getDialCodeFromCountryCode(props.countries, newValue as string) ?? ''

    props.onValueChange(`${currentDialCode}${phoneNumber ?? ''}`)
  }

  const filteredOptions = !filterValue
    ? options
    : options.filter((option) => option.label.toLowerCase().includes(filterValue.toLowerCase()))

  /*
     User can enter phone number together with country code.
     So we are going to parse entered value and check if country code was entered.
     And update country code and phone number accordingly
   */
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e
    const countryCodeFromInput = phoneNumberHelpers.getCountryCode(value) ?? ''
    const currentDialCode =
      getDialCodeFromCountryCode(props.countries, countryCodeFromInput || phoneNumberCode) ?? ''
    const formattedInput = phoneNumberHelpers.formatPhoneNumber(
      `${currentDialCode}${value.replace(currentDialCode, '')}`
    )

    if (countryCodeFromInput) {
      setPhoneNumberCode(countryCodeFromInput)
    }

    setPhoneNumber(formattedInput.replace(currentDialCode, ''))
    props.onValueChange(formattedInput ?? '')
  }

  useEffect(() => {
    if (phoneNumber === undefined) {
      const countryCodeFromInput =
        (phoneNumberHelpers.getCountryCode(props.value || '') ??
          getDefaultCountry(props.countries, props?.defaultCountry)?.code) ||
        ''

      const currentDialCode =
        getDialCodeFromCountryCode(props.countries, countryCodeFromInput) ?? ''
      const formattedInput =
        phoneNumberHelpers.formatPhoneNumber(
          `${currentDialCode}${(props.value || '').replace(currentDialCode, '')}`
        ) ?? ''
      setPhoneNumberCode(countryCodeFromInput)
      setPhoneNumber(formattedInput.replace(currentDialCode, ''))
    }
  }, [props.value, phoneNumber, props.defaultCountry, props.countries])

  const hasError = props.errorMessage !== undefined && props.errorMessage !== ''

  return (
    <div className="gap-y-1">
      <div className="relative flex w-full flex-row gap-x-2.5">
        <div className="flex w-full max-w-[110px] flex-col">
          <MenuContainerComponent
            name={props.name}
            onSelectOptionHandler={onSelectOptionHandler}
            options={filteredOptions}
            setActiveMenuOption={setActiveMenuOption}
            activeMenuOption={activeMenuOption}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            value={props.value}
            disabled={props.disabled}
          >
            <TextFieldContainer
              status={props.status}
              isFilled={isFilled}
              disabled={props.disabled}
              styleType={props.styleType}
              isFocused={isFocused}
              name={props.name}
            >
              <div className=" w-full">
                {isFocused ? (
                  <input
                    className={textInput()}
                    onChange={onChangeFilterInputValue}
                    onFocus={(e) => e.target.select()}
                    value={filterValue}
                    autoFocus
                    id={`filter-input-${props.name}`}
                  />
                ) : (
                  displaySelectedOption(props.countries, phoneNumberCode)
                )}

                <div
                  className={twMerge(
                    'absolute right-0 top-0 flex h-full items-center px-2 transition',
                    isFocused && 'rotate-180'
                  )}
                >
                  <Icon imgSrc={IMAGE.icons.small.action.chevronDown_24} />
                </div>
              </div>
            </TextFieldContainer>
            {isFocused && (
              <OptionsContainerComponent
                activeMenuOption={activeMenuOption}
                name={props.name}
                onSelectOptionHandler={onSelectOptionHandler}
                options={filteredOptions}
                setActiveMenuOption={setActiveMenuOption}
              />
            )}
          </MenuContainerComponent>
        </div>
        <div className="w-full flex-auto">
          <TextField
            id={`${props.id}Input`}
            status={props.status ?? (hasError ? 'error' : undefined)}
            type="tel"
            onChange={handlePhoneNumberChange}
            value={phoneNumber}
            disabled={props.disabled}
            label={props.label}
            name={`${props.name}Input`}
            styleType={props.styleType}
            autoFocus={props.autoFocus}
            fieldRef={props.fieldRef}
          />
        </div>
      </div>

      {(props.status === 'error' || hasError) && (
        <p className={caption({ color: FORM_ELEMENT.errorColorName })}>{props.errorMessage}</p>
      )}
    </div>
  )
}

export default PhoneNumberInput
